// frontend/src/pages/Reports/ReportGeneratorCR.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Form, Select, Button, DatePicker, Radio, Checkbox,
    Typography, Row, Col, Space, Table, Tag, Divider, Empty,
    Alert, Spin, Skeleton, Tooltip
} from 'antd';
import {
    FileTextOutlined, FileWordOutlined, FilePdfOutlined,
    RollbackOutlined, EyeOutlined, LinkOutlined,
    ClockCircleOutlined, UserOutlined, InfoCircleOutlined,
    CheckCircleOutlined, CloseOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useNotificationApi } from '../../context/NotificationContext';
import './ReportGeneratorCR.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const DATE_FORMAT = 'DD/MM/YYYY';

const ReportGeneratorCR = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const notificationApi = useNotificationApi();
    const [configForm] = Form.useForm();

    // Report type
    const [reportType, setReportType] = useState('single'); // 'single' | 'monthly'
    const [format, setFormat] = useState('docx'); // 'docx' | 'pdf'

    // Data
    const [contracts, setContracts] = useState([]);
    const [contractsLoading, setContractsLoading] = useState(false);

    // Ticket types (loaded from backend so dev/prod IDs stay in sync)
    const [ticketTypes, setTicketTypes] = useState([]);
    const [ticketTypesLoading, setTicketTypesLoading] = useState(false);

    // Selected values from form watches
    const selectedContract = Form.useWatch('contractId', configForm);
    const selectedMonth = Form.useWatch('month', configForm);
    const selectedDateRange = Form.useWatch('dateRange', configForm);
    const selectedTicketTypes = Form.useWatch('ticketTypes', configForm);
    const includeAllEntries = Form.useWatch('includeAllEntries', configForm);
    const includeZeroEntries = Form.useWatch('includeZeroEntries', configForm);

    // Single CR mode — the CR list table
    const [crList, setCrList] = useState([]);
    const [crListLoading, setCrListLoading] = useState(false);
    const [crPage, setCrPage] = useState(1);
    const PAGE_SIZE = 5;

    // Selected CR (from clicking a row in the CR list)
    const [selectedCRId, setSelectedCRId] = useState(null);

    // Previews
    const [crPreview, setCrPreview] = useState(null);
    const [crPreviewLoading, setCrPreviewLoading] = useState(false);
    const [periodPreview, setPeriodPreview] = useState(null);
    const [periodPreviewLoading, setPeriodPreviewLoading] = useState(false);

    // Generated report
    const [reportData, setReportData] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [exporting, setExporting] = useState(false);

    // ------------------------------------------------------------
    // Notification helpers
    // ------------------------------------------------------------
    const showError = useCallback((title, content) => {
        notificationApi.error({
            message: title, description: content,
            duration: 4.5, placement: 'topRight', style: { width: 420 }
        });
    }, [notificationApi]);

    const showSuccess = useCallback((title, content) => {
        notificationApi.success({
            message: title, description: content,
            duration: 3, placement: 'topRight', style: { width: 420 }
        });
    }, [notificationApi]);

    // ------------------------------------------------------------
    // Load contracts on mount
    // ------------------------------------------------------------
    useEffect(() => {
        const fetchContracts = async () => {
            setContractsLoading(true);
            try {
                const res = await api.get('/api/contracts');
                if (res.data.success) setContracts(res.data.data || []);
            } catch (err) {
                console.error('[ReportGeneratorCR] Contracts fetch failed:', err);
                showError('Failed to Load', 'Could not load contracts.');
            } finally {
                setContractsLoading(false);
            }
        };
        fetchContracts();
    }, [showError]);

    // Load ticket types from backend
    useEffect(() => {
        const fetchTicketTypes = async () => {
            setTicketTypesLoading(true);
            try {
                const res = await api.get('/api/ticket-types');
                if (res.data.success) setTicketTypes(res.data.data || []);
            } catch (err) {
                console.error('[ReportGeneratorCR] Ticket types fetch failed:', err);
                // non-fatal — dropdown just won't have options
            } finally {
                setTicketTypesLoading(false);
            }
        };
        fetchTicketTypes();
    }, []);

    // ------------------------------------------------------------
    // Reset on mode change
    // ------------------------------------------------------------
    useEffect(() => {
        configForm.resetFields();
        setCrList([]);
        setCrPage(1);
        setSelectedCRId(null);
        setCrPreview(null);
        setPeriodPreview(null);
        setReportData(null);
    }, [reportType, configForm]);

    // ------------------------------------------------------------
    // SINGLE CR MODE — fetch CR list when contract + month are set
    // ------------------------------------------------------------
    useEffect(() => {
        const fetchCRList = async () => {
            // Clear any generated report when Contract or Month changes
            setReportData(null);

            if (
                reportType !== 'single' ||
                !selectedContract ||
                !selectedMonth
            ) {
                setCrList([]);
                setSelectedCRId(null);
                return;
            }
            setCrListLoading(true);
            try {
                const start = selectedMonth.startOf('month').format('YYYY-MM-DD');
                const end = selectedMonth.endOf('month').format('YYYY-MM-DD');
                const res = await api.get('/api/crs', {
                    params: {
                        contractId: selectedContract,
                        fromDate: start,
                        toDate: end,
                        page: 1,
                        limit: 200,
                        sortBy: 'requestDate:DESC'
                    }
                });
                if (res.data.success) {
                    setCrList(res.data.data || []);
                    setSelectedCRId(null);
                } else {
                    setCrList([]);
                }
            } catch (err) {
                console.error('[ReportGeneratorCR] CR list fetch failed:', err);
                setCrList([]);
            } finally {
                setCrListLoading(false);
            }
        };
        fetchCRList();
    }, [reportType, selectedContract, selectedMonth]);

    // ------------------------------------------------------------
    // SINGLE CR MODE — fetch CR preview when a CR is selected
    // ------------------------------------------------------------
    useEffect(() => {
        const fetchPreview = async () => {
            // Clear any previously-generated report when the selection changes
            setReportData(null);

            if (reportType !== 'single' || !selectedCRId) {
                setCrPreview(null);
                return;
            }
            setCrPreviewLoading(true);
            try {
                const res = await api.get(`/api/time-tracking/report/single/${selectedCRId}`);
                if (res.data.success) {
                    setCrPreview(res.data.data);
                } else {
                    setCrPreview(null);
                }
            } catch (err) {
                console.error('[ReportGeneratorCR] CR preview failed:', err);
                setCrPreview(null);
            } finally {
                setCrPreviewLoading(false);
            }
        };
        fetchPreview();
    }, [reportType, selectedCRId]);

    // ------------------------------------------------------------
    // MONTHLY MODE — period preview
    // ------------------------------------------------------------
    useEffect(() => {
        const fetchPreview = async () => {
            // Clear any generated report when filters change
            setReportData(null);

            if (
                reportType !== 'monthly' ||
                !selectedContract ||
                !selectedDateRange?.[0] ||
                !selectedDateRange?.[1]
            ) {
                setPeriodPreview(null);
                return;
            }
            setPeriodPreviewLoading(true);
            try {
                const params = {
                    contractId: selectedContract,
                    startDate: selectedDateRange[0].format('YYYY-MM-DD'),
                    endDate: selectedDateRange[1].format('YYYY-MM-DD')
                };
                if (selectedTicketTypes && selectedTicketTypes.length > 0) {
                    params.ticketTypes = selectedTicketTypes.join(',');
                }
                if (includeAllEntries) params.includeAllEntries = 'true';
                if (includeZeroEntries) params.includeZeroEntries = 'true';

                const res = await api.get('/api/time-tracking/report/monthly', { params });
                if (res.data.success) {
                    setPeriodPreview(res.data.data);
                } else {
                    setPeriodPreview(null);
                }
            } catch (err) {
                console.error('[ReportGeneratorCR] Period preview failed:', err);
                setPeriodPreview(null);
            } finally {
                setPeriodPreviewLoading(false);
            }
        };
        fetchPreview();
    }, [
        reportType, selectedContract, selectedDateRange,
        selectedTicketTypes, includeAllEntries, includeZeroEntries
    ]);

    // ------------------------------------------------------------
    // Ticket Type exclusive ALL logic
    // ------------------------------------------------------------
    const handleTicketTypesChange = (values) => {
        const lastPicked = values[values.length - 1];
        if (lastPicked === 'ALL') {
            configForm.setFieldsValue({ ticketTypes: ['ALL'] });
            return;
        }
        if (values.includes('ALL') && values.length > 1) {
            configForm.setFieldsValue({
                ticketTypes: values.filter(v => v !== 'ALL')
            });
        }
    };

    // ------------------------------------------------------------
    // Reset the page (Cancel button)
    // ------------------------------------------------------------
    const handleReset = () => {
        configForm.resetFields();
        setCrList([]);
        setCrPage(1);
        setSelectedCRId(null);
        setCrPreview(null);
        setPeriodPreview(null);
        setReportData(null);
    };

    // ------------------------------------------------------------
    // Generate report
    // ------------------------------------------------------------
    const handleGenerate = async () => {
        let values;
        try {
            values = await configForm.validateFields();
        } catch {
            showError('Missing Fields', 'Please fill in all required fields.');
            return;
        }

        if (reportType === 'single' && !selectedCRId) {
            showError('No CR Selected', 'Please click a CR from the list to select it.');
            return;
        }

        setGenerating(true);
        try {
            if (reportType === 'single') {
                const res = await api.get(
                    `/api/time-tracking/report/single/${selectedCRId}`
                );
                if (res.data.success) {
                    setReportData({ type: 'single', data: res.data.data });
                    showSuccess('Report Ready', 'CR report generated successfully.');
                } else {
                    showError('Generation Failed', res.data.message || 'Failed to generate.');
                }
            } else {
                const params = {
                    contractId: values.contractId,
                    startDate: values.dateRange[0].format('YYYY-MM-DD'),
                    endDate: values.dateRange[1].format('YYYY-MM-DD')
                };
                if (values.ticketTypes?.length) {
                    params.ticketTypes = values.ticketTypes.join(',');
                }
                if (values.includeAllEntries) params.includeAllEntries = 'true';
                if (values.includeZeroEntries) params.includeZeroEntries = 'true';

                const res = await api.get('/api/time-tracking/report/monthly', { params });
                if (res.data.success) {
                    setReportData({ type: 'monthly', data: res.data.data });
                    showSuccess('Report Ready', 'Monthly report generated successfully.');
                } else {
                    showError('Generation Failed', res.data.message || 'Failed to generate.');
                }
            }
        } catch (err) {
            console.error('[ReportGeneratorCR] Generate failed:', err);
            showError('Generation Failed', err.response?.data?.message || 'An error occurred.');
        } finally {
            setGenerating(false);
        }
    };

    // ------------------------------------------------------------
    // Export
    // ------------------------------------------------------------
    const handleExport = async (selectedFormat) => {
        if (!reportData) return;
        setExporting(true);
        try {
            let response;

            if (reportData.type === 'single') {
                response = await api.post('/api/reports/time-tracking/cr/single', {
                    crMasterId: selectedCRId,
                    format: selectedFormat
                }, { responseType: 'blob' });
            } else {
                const values = configForm.getFieldsValue();
                const contractId = values.contractId;
                response = await api.post('/api/reports/time-tracking/cr/monthly', {
                    contractId,
                    startDate: values.dateRange[0].format('YYYY-MM-DD'),
                    endDate: values.dateRange[1].format('YYYY-MM-DD'),
                    ticketTypes: values.ticketTypes,
                    includeAllEntries: values.includeAllEntries || false,
                    includeZeroEntries: values.includeZeroEntries || false,
                    format: selectedFormat
                }, { responseType: 'blob' });
            }

            if (response.data instanceof Blob) {
                // Prefer the server's Content-Disposition filename if present,
                // otherwise fall back to a generic name.
                let filename = `CR_Report.${selectedFormat}`;
                const disposition = response.headers?.['content-disposition'] || '';
                const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
                if (match) {
                    const raw = match[1] || match[2];
                    try {
                        filename = decodeURIComponent(raw);
                    } catch {
                        filename = raw;
                    }
                }

                const url = window.URL.createObjectURL(response.data);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                showSuccess('Download Successful', `${filename}`);
            }
        } catch (err) {
            console.error('[ReportGeneratorCR] Export failed:', err);
            showError('Export Failed', err.response?.data?.message || 'Could not export.');
        } finally {
            setExporting(false);
        }
    };

    // ------------------------------------------------------------
    // CR list columns (single mode)
    // ------------------------------------------------------------
    const crListColumns = useMemo(() => [
        {
            title: 'CR NUMBER',
            dataIndex: 'crNumber',
            key: 'crNumber',
            width: 200,
            render: (text) => (
                <Text strong style={{
                    fontFamily: "'SF Mono', 'Monaco', 'Consolas', monospace",
                    color: '#4f46e5'
                }}>
                    {text || '—'}
                </Text>
            )
        },
        {
            title: 'TITLE',
            dataIndex: 'title',
            key: 'title',
            ellipsis: true,
            render: (text) => text || <Text type="secondary">(no title)</Text>
        },
        {
            title: 'SERVICE CATEGORY',
            dataIndex: 'crTypeName',
            key: 'crTypeName',
            width: 180,
            render: (name) => name ? <Tag color="blue">{name}</Tag> : <Tag>—</Tag>
        },
        {
            title: 'REQUEST DATE',
            dataIndex: 'requestDate',
            key: 'requestDate',
            width: 140,
            render: (date) => date ? dayjs(date).format(DATE_FORMAT) : '—'
        },
        {
            title: 'HOURS',
            dataIndex: 'totalHours',
            key: 'totalHours',
            width: 100,
            align: 'right',
            render: (v) => <Text strong>{parseFloat(v || 0).toFixed(2)}</Text>
        }
    ], []);

    // ------------------------------------------------------------
    // Table columns — single CR entries
    // ------------------------------------------------------------
    const singleEntryColumns = useMemo(() => [
        { title: 'Date', dataIndex: 'entry_date', key: 'entry_date', width: 110,
          render: (v) => v ? dayjs(v).format('DD MMM YYYY') : '—' },
        { title: 'Start', dataIndex: 'start_time', key: 'start_time', width: 90,
          render: (v) => v ? dayjs(v, 'HH:mm:ss').format('hh:mm A') : '—' },
        { title: 'End', dataIndex: 'end_time', key: 'end_time', width: 90,
          render: (v) => v ? dayjs(v, 'HH:mm:ss').format('hh:mm A') : '—' },
        { title: 'Duration', dataIndex: 'duration', key: 'duration', width: 100, align: 'right',
          render: (v) => v ? <Text strong>{parseFloat(v).toFixed(2)} hrs</Text> : '—' },
        { title: 'Activity Type', dataIndex: 'activity_type', key: 'activity_type', width: 170,
          render: (v) => v ? <Tag color="cyan">{v}</Tag> : '—' },
        { title: 'Resource(s)', dataIndex: 'resource', key: 'resource' },
        { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true }
    ], []);

    // ------------------------------------------------------------
    // Table columns — monthly summary (per ticket type)
    // ------------------------------------------------------------
    const buildMonthlyColumns = (prefix) => [
        { title: 'CR/CM #', dataIndex: `${prefix}_number`, key: `${prefix}_number`, width: 160,
          render: (v) => v ? <Text strong>{v}</Text> : '—' },
        { title: 'Description', dataIndex: `${prefix}_description`, key: `${prefix}_description`, ellipsis: true,
          render: (v) => v || '—' },
        { title: 'Link Ticket(s)', dataIndex: `${prefix}_linked_tickets`, key: `${prefix}_linked_tickets`, width: 220,
          render: (v) => {
              if (!v) return <Text type="secondary">—</Text>;
              const items = String(v).split('\n').filter(Boolean);
              return (
                  <div>
                      {items.map((t, i) => (
                          <div key={i}><Text code style={{ fontSize: 12 }}>{t}</Text></div>
                      ))}
                  </div>
              );
          } },
        { title: 'Request Date', dataIndex: `${prefix}_request_date`, key: `${prefix}_request_date`, width: 120 },
        { title: 'Entries', dataIndex: `${prefix}_entry_count`, key: `${prefix}_entry_count`, width: 80, align: 'center',
          render: (v) => <Tag color="blue">{v || 0}</Tag> },
        { title: 'Total Hours', dataIndex: `${prefix}_total_hours`, key: `${prefix}_total_hours`, width: 110, align: 'right',
          render: (v) => <Text strong>{parseFloat(v || 0).toFixed(2)} hrs</Text> }
    ];

    // ------------------------------------------------------------
    // Render
    // ------------------------------------------------------------
    return (
        <div className="rcr-page">
            <div className="rcr-header">
                <div className="rcr-header-left">
                    <Title level={2} className="rcr-title">
                        <FileTextOutlined /> CR Reports
                    </Title>
                    <Text type="secondary" className="rcr-subtitle">
                        Generate a detailed report for a single CR, or a consolidated report for all CRs within a period.
                    </Text>
                </div>
                <Button
                    icon={<RollbackOutlined />}
                    size="large"
                    onClick={() => navigate('/reports')}
                    className="rcr-back-btn"
                >
                    Back to Reports Hub
                </Button>
            </div>

            <Form form={configForm} layout="vertical" name="cr_report_form">
                {/* ============ CARD 1: CONFIGURATION ============ */}
                <Card className="rcr-section-card" bordered={false}>
                    <div className="rcr-section-header">
                        <div className="rcr-section-icon-wrapper">
                            <FileTextOutlined className="rcr-section-icon" />
                        </div>
                        <div>
                            <div className="rcr-section-title">Report Configuration</div>
                            <div className="rcr-section-hint">
                                Choose the report type and configure the criteria below.
                            </div>
                        </div>
                    </div>

                    {/* Report Type toggle */}
                    <Form.Item label="Report Type" style={{ marginBottom: 24 }}>
                        <Radio.Group
                            value={reportType}
                            onChange={(e) => setReportType(e.target.value)}
                            buttonStyle="solid"
                            size="large"
                        >
                            <Radio.Button value="single">Single CR</Radio.Button>
                            <Radio.Button value="monthly">Monthly / Period</Radio.Button>
                        </Radio.Group>
                    </Form.Item>

                    {/* ============ SINGLE CR MODE ============ */}
                    {reportType === 'single' && (
                        <>
                            <Row gutter={20}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="contractId"
                                        label="Contract"
                                        rules={[{ required: true, message: 'Please select a contract' }]}
                                    >
                                        <Select
                                            placeholder="Select contract"
                                            showSearch
                                            optionFilterProp="children"
                                            loading={contractsLoading}
                                            size="large"
                                        >
                                            {contracts.map(c => (
                                                <Option key={c.id} value={c.id}>
                                                    {c.contractName || `Contract #${c.id}`}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="month"
                                        label="Month"
                                        rules={[{ required: true, message: 'Please select a month' }]}
                                        tooltip="Shows CRs with a request date in this month"
                                    >
                                        <DatePicker
                                            picker="month"
                                            format="MMMM YYYY"
                                            style={{ width: '100%' }}
                                            size="large"
                                            placeholder="Select month"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* CR list table */}
                            {selectedContract && selectedMonth && (
                                <div style={{ marginTop: 12, marginBottom: 20 }}>
                                    <div style={{ marginBottom: 12 }}>
                                        <Text strong>
                                            CRs created in {selectedMonth.format('MMMM YYYY')}
                                        </Text>
                                        <Text type="secondary" style={{ marginLeft: 8, fontSize: 13 }}>
                                            (click a row to select)
                                        </Text>
                                    </div>
                                    <Table
                                        columns={crListColumns}
                                        dataSource={crList}
                                        rowKey="id"
                                        loading={crListLoading}
                                        size="small"
                                        bordered
                                        pagination={{
                                            current: crPage,
                                            pageSize: PAGE_SIZE,
                                            total: crList.length,
                                            onChange: setCrPage,
                                            showSizeChanger: false
                                        }}
                                        onRow={(record) => ({
                                            onClick: () => setSelectedCRId(record.id),
                                            style: {
                                                cursor: 'pointer',
                                                background: selectedCRId === record.id ? '#eef2ff' : undefined
                                            }
                                        })}
                                        rowClassName={(record) =>
                                            selectedCRId === record.id ? 'rcr-selected-row' : ''
                                        }
                                        locale={{
                                            emptyText: (
                                                <Empty
                                                    description={`No CRs found for this contract in ${selectedMonth.format('MMMM YYYY')}`}
                                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                />
                                            )
                                        }}
                                    />
                                </div>
                            )}

                            {/* CR Preview */}
                            {selectedCRId && (
                                <div className="rcr-preview-card">
                                    <div className="rcr-preview-title">
                                        <InfoCircleOutlined /> Selected CR Preview
                                    </div>
                                    {crPreviewLoading ? (
                                        <Skeleton active paragraph={{ rows: 4 }} />
                                    ) : crPreview ? (
                                        <>
                                            <Row gutter={16}>
                                                <Col xs={24} md={12}>
                                                    <div className="rcr-preview-field">
                                                        <Text type="secondary" className="rcr-preview-label">CR Number</Text>
                                                        <div><Text strong className="rcr-mono">{crPreview.header?.cr_number}</Text></div>
                                                    </div>
                                                    <div className="rcr-preview-field">
                                                        <Text type="secondary" className="rcr-preview-label">Service Category</Text>
                                                        <div>
                                                            {crPreview.header?.cr_type_name && crPreview.header.cr_type_name !== 'N/A'
                                                                ? <Tag color="blue">{crPreview.header.cr_type_name}</Tag>
                                                                : <Tag>—</Tag>}
                                                        </div>
                                                    </div>
                                                    <div className="rcr-preview-field">
                                                        <Text type="secondary" className="rcr-preview-label">Title</Text>
                                                        <div>{crPreview.header?.title || '—'}</div>
                                                    </div>
                                                    <div className="rcr-preview-field">
                                                        <Text type="secondary" className="rcr-preview-label">Description</Text>
                                                        <div>{crPreview.header?.description || '—'}</div>
                                                    </div>
                                                </Col>
                                                <Col xs={24} md={12}>
                                                    <div className="rcr-preview-field">
                                                        <Text type="secondary" className="rcr-preview-label">Request Date</Text>
                                                        <div>{crPreview.header?.request_date || '—'}</div>
                                                    </div>
                                                    <div className="rcr-preview-field">
                                                        <Text type="secondary" className="rcr-preview-label">Total Entries</Text>
                                                        <div><Tag color="blue">{crPreview.entries?.length || 0}</Tag></div>
                                                    </div>
                                                    <div className="rcr-preview-field">
                                                        <Text type="secondary" className="rcr-preview-label">Total Hours</Text>
                                                        <div><Text strong style={{ color: '#0284c7' }}>{crPreview.total_hours || 0} hrs</Text></div>
                                                    </div>
                                                    <div className="rcr-preview-field">
                                                        <Text type="secondary" className="rcr-preview-label">Man-Days</Text>
                                                        <div><Text strong style={{ color: '#d97706' }}>{crPreview.man_days || 0} days</Text></div>
                                                    </div>
                                                </Col>
                                            </Row>

                                            {/* Linked Tickets */}
                                            {crPreview.linkedTickets && crPreview.linkedTickets.length > 0 && (
                                                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
                                                    <Text type="secondary" className="rcr-preview-label">
                                                        Linked Tickets ({crPreview.linkedTickets.length})
                                                    </Text>
                                                    <div style={{ marginTop: 8 }}>
                                                        {crPreview.linkedTickets.map((t, i) => (
                                                            <div key={i} style={{ marginBottom: 6 }}>
                                                                <Text code style={{ fontSize: 12 }}>{t.ticket_uuid}</Text>
                                                                <Text style={{ marginLeft: 8, fontSize: 13 }}>{t.title}</Text>
                                                                {t.ticket_type && t.ticket_type !== 'N/A' && (
                                                                    <Tag color="blue" style={{ marginLeft: 8 }}>{t.ticket_type}</Tag>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <Empty description="No preview available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {/* ============ MONTHLY MODE ============ */}
                    {reportType === 'monthly' && (
                        <>
                            <Row gutter={20}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="contractId"
                                        label="Contract"
                                        rules={[{ required: true, message: 'Please select a contract' }]}
                                    >
                                        <Select
                                            placeholder="Select contract"
                                            showSearch
                                            optionFilterProp="children"
                                            loading={contractsLoading}
                                            size="large"
                                        >
                                            {contracts.map(c => (
                                                <Option key={c.id} value={c.id}>
                                                    {c.contractName || `Contract #${c.id}`}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="dateRange"
                                        label="Date Range"
                                        rules={[{ required: true, message: 'Please select a date range' }]}
                                    >
                                        <RangePicker
                                            style={{ width: '100%' }}
                                            format={DATE_FORMAT}
                                            size="large"
                                            placeholder={['From date', 'To date']}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={20}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="ticketTypes"
                                        label="Service Category"
                                        rules={[{ required: true, message: 'Please select at least one service category' }]}
                                    >
                                        <Select
                                            mode="multiple"
                                            placeholder="Select service category(ies)"
                                            size="large"
                                            maxTagCount="responsive"
                                            allowClear
                                            loading={ticketTypesLoading}
                                            onChange={handleTicketTypesChange}
                                        >
                                            <Option key="ALL" value="ALL">All Types</Option>
                                            {ticketTypes.map(t => (
                                                <Option key={t.id} value={t.id}>{t.name}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* Report options */}
                            <div className="rcr-options-group">
                                <div className="rcr-options-title">Report Options</div>
                                <Form.Item name="includeAllEntries" valuePropName="checked" style={{ marginBottom: 8 }}>
                                    <Checkbox>
                                        Include all time entries for all CRs
                                        <Text type="secondary" className="rcr-option-hint">
                                            (Date range only filters which CRs appear; all entries are included)
                                        </Text>
                                    </Checkbox>
                                </Form.Item>
                                <Form.Item name="includeZeroEntries" valuePropName="checked" style={{ marginBottom: 0 }}>
                                    <Checkbox>
                                        Include CRs with no time entries
                                        <Text type="secondary" className="rcr-option-hint">
                                            (Show CRs with zero entries as 0 / —)
                                        </Text>
                                    </Checkbox>
                                </Form.Item>
                            </div>

                            {/* Period preview */}
                            {selectedContract && selectedDateRange?.[0] && selectedDateRange?.[1] && (
                                <div className="rcr-preview-card">
                                    <div className="rcr-preview-title">
                                        <InfoCircleOutlined /> Period Preview
                                    </div>
                                    {periodPreviewLoading ? (
                                        <Skeleton active paragraph={{ rows: 3 }} />
                                    ) : periodPreview ? (
                                        <>
                                            <Row gutter={16}>
                                                <Col xs={12} md={6}>
                                                    <div className="rcr-preview-stat">
                                                        <Text type="secondary" className="rcr-preview-label">Total CRs</Text>
                                                        <div className="rcr-preview-stat-value">
                                                            {(
                                                                (periodPreview.cr_summary?.length || 0) +
                                                                (periodPreview.cm_summary?.length || 0) +
                                                                (periodPreview.pm_summary?.length || 0) +
                                                                (periodPreview.oth_summary?.length || 0)
                                                            )}
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col xs={12} md={6}>
                                                    <div className="rcr-preview-stat">
                                                        <Text type="secondary" className="rcr-preview-label">Total Hours</Text>
                                                        <div className="rcr-preview-stat-value rcr-stat-hours">
                                                            {parseFloat(periodPreview.grand_total || 0).toFixed(2)}
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col xs={12} md={6}>
                                                    <div className="rcr-preview-stat">
                                                        <Text type="secondary" className="rcr-preview-label">Man-Days</Text>
                                                        <div className="rcr-preview-stat-value rcr-stat-days">
                                                            {Math.ceil((periodPreview.grand_total || 0) / 8)}
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                            <Divider style={{ margin: '16px 0' }} />
                                            <div className="rcr-preview-breakdown">
                                                {periodPreview.cr_total_hours > 0 && (
                                                    <Tag color="blue">CR: {periodPreview.cr_total_hours} hrs</Tag>
                                                )}
                                                {periodPreview.cm_total_hours > 0 && (
                                                    <Tag color="green">CM: {periodPreview.cm_total_hours} hrs</Tag>
                                                )}
                                                {periodPreview.pm_total_hours > 0 && (
                                                    <Tag color="gold">PM: {periodPreview.pm_total_hours} hrs</Tag>
                                                )}
                                                {periodPreview.oth_total_hours > 0 && (
                                                    <Tag color="default">NSLACM: {periodPreview.oth_total_hours} hrs</Tag>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <Empty description="No data for this period" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </Card>

                {/* ============ ACTIONS BAR ============ */}
                <div className="rcr-actions-bar">
                    <div className="rcr-format-toggle">
                        <Text strong style={{ marginRight: 12 }}>Format:</Text>
                        <Radio.Group value={format} onChange={(e) => setFormat(e.target.value)} buttonStyle="solid">
                            <Radio.Button value="docx">
                                <FileWordOutlined /> DOCX
                            </Radio.Button>
                            <Radio.Button value="pdf">
                                <FilePdfOutlined /> PDF
                            </Radio.Button>
                        </Radio.Group>
                    </div>
                    <Space>
                        <Button size="large" onClick={handleReset} disabled={generating}>
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            icon={<EyeOutlined />}
                            loading={generating}
                            onClick={handleGenerate}
                            className="rcr-generate-btn"
                        >
                            Generate Report
                        </Button>
                    </Space>
                </div>
            </Form>

            {/* ============ CARD 2: REPORT PREVIEW ============ */}
            {reportData && (
                <Card className="rcr-section-card rcr-preview-section" bordered={false}>
                    <div className="rcr-section-header">
                        <div className="rcr-section-icon-wrapper">
                            <CheckCircleOutlined className="rcr-section-icon" />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div className="rcr-section-title">Report Preview</div>
                            <div className="rcr-section-hint">
                                Review below, then download in your preferred format.
                            </div>
                        </div>
                        <Space>
                            <Button
                                icon={<FileWordOutlined />}
                                onClick={() => handleExport('docx')}
                                loading={exporting}
                                size="large"
                            >
                                Download DOCX
                            </Button>
                            <Button
                                icon={<FilePdfOutlined />}
                                onClick={() => handleExport('pdf')}
                                loading={exporting}
                                size="large"
                            >
                                Download PDF
                            </Button>
                        </Space>
                    </div>

                    {reportData.type === 'single' && reportData.data && (
                        <div className="rcr-report-content">
                            <div className="rcr-report-header">
                                <Title level={3} style={{ margin: 0 }}>
                                    {reportData.data.header?.contract_name || 'CR Report'}
                                </Title>
                                <Text type="secondary">{reportData.data.header?.cr_number}</Text>
                            </div>

                            <div className="rcr-report-meta">
                                <Row gutter={[16, 12]}>
                                    <Col xs={24} md={12}>
                                        <div className="rcr-meta-item">
                                            <Text type="secondary">CR Number</Text>
                                            <div><Text strong style={{ color: '#4f46e5' }}>{reportData.data.header?.cr_number}</Text></div>
                                        </div>
                                        <div className="rcr-meta-item">
                                            <Text type="secondary">Service Category</Text>
                                            <div>
                                                {reportData.data.header?.cr_type_name && reportData.data.header.cr_type_name !== 'N/A'
                                                    ? <Tag color="blue">{reportData.data.header.cr_type_name}</Tag>
                                                    : <Tag>—</Tag>}
                                            </div>
                                        </div>
                                        <div className="rcr-meta-item">
                                            <Text type="secondary">Description</Text>
                                            <div>{reportData.data.header?.description || '—'}</div>
                                        </div>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <div className="rcr-meta-item">
                                            <Text type="secondary">Request Date</Text>
                                            <div><Text strong>{reportData.data.header?.request_date || '—'}</Text></div>
                                        </div>
                                        <div className="rcr-meta-item">
                                            <Text type="secondary">Target Production Date</Text>
                                            <div><Text strong>{reportData.data.header?.target_production_date || '—'}</Text></div>
                                        </div>
                                        <div className="rcr-meta-item">
                                            <Text type="secondary">Estimated Man-Days</Text>
                                            <div><Text strong>{reportData.data.header?.estimated_mandays || '—'}</Text></div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* Linked Tickets */}
                            {reportData.data.linkedTickets && reportData.data.linkedTickets.length > 0 && (
                                <div className="rcr-report-meta" style={{ marginTop: 16 }}>
                                    <Text type="secondary" className="rcr-preview-label">
                                        Linked Tickets ({reportData.data.linkedTickets.length})
                                    </Text>
                                    <div style={{ marginTop: 8 }}>
                                        {reportData.data.linkedTickets.map((t, i) => (
                                            <div key={i} style={{ marginBottom: 6 }}>
                                                <Text code style={{ fontSize: 12 }}>{t.ticket_uuid}</Text>
                                                <Text style={{ marginLeft: 8, fontSize: 13 }}>{t.title}</Text>
                                                {t.ticket_type && t.ticket_type !== 'N/A' && (
                                                    <Tag color="blue" style={{ marginLeft: 8 }}>{t.ticket_type}</Tag>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="rcr-report-section-title">Time Entries</div>
                            {reportData.data.entries?.length > 0 ? (
                                <Table
                                    columns={singleEntryColumns}
                                    dataSource={reportData.data.entries}
                                    rowKey={(r, i) => i}
                                    pagination={false}
                                    size="small"
                                    bordered
                                    scroll={{ x: 'max-content' }}
                                    className="rcr-report-table"
                                />
                            ) : (
                                <Empty description="No time entries" />
                            )}

                            <div className="rcr-report-totals">
                                <div className="rcr-total-item">
                                    <Text type="secondary">Total Hours</Text>
                                    <div className="rcr-total-value rcr-stat-hours">
                                        {reportData.data.total_hours || 0} hrs
                                    </div>
                                </div>
                                <div className="rcr-total-divider" />
                                <div className="rcr-total-item">
                                    <Text type="secondary">Man Days</Text>
                                    <div className="rcr-total-value rcr-stat-days">
                                        {reportData.data.man_days || 0} days
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {reportData.type === 'monthly' && reportData.data && (
                        <div className="rcr-report-content">
                            <div className="rcr-report-header">
                                <Title level={3} style={{ margin: 0 }}>
                                    CR Report — {reportData.data.contract_name}
                                </Title>
                                <Text type="secondary">
                                    {configForm.getFieldValue('dateRange')?.[0]?.format('DD MMM YYYY')} — {configForm.getFieldValue('dateRange')?.[1]?.format('DD MMM YYYY')}
                                </Text>
                            </div>

                            <Alert
                                type="info"
                                showIcon
                                className="rcr-grand-total"
                                message={
                                    <div className="rcr-grand-total-content">
                                        <div>
                                            <Text strong>Grand Total: </Text>
                                            <Text strong className="rcr-stat-hours" style={{ fontSize: 18 }}>
                                                {reportData.data.grand_total || 0} hrs
                                            </Text>
                                        </div>
                                        <div>
                                            <Text strong>Man Days: </Text>
                                            <Text strong className="rcr-stat-days" style={{ fontSize: 18 }}>
                                                {reportData.data.grand_man_days || 0} days
                                            </Text>
                                        </div>
                                    </div>
                                }
                            />

                            {[
                                { key: 'cr',  title: 'ACTIVITY CR',     color: 'blue',    data: reportData.data.cr_summary },
                                { key: 'cm',  title: 'ACTIVITY CM',     color: 'green',   data: reportData.data.cm_summary },
                                { key: 'pm',  title: 'ACTIVITY PM',     color: 'gold',    data: reportData.data.pm_summary },
                                { key: 'oth', title: 'ACTIVITY NSLACM', color: 'default', data: reportData.data.oth_summary }
                            ].map(section => {
                                if (!section.data || section.data.length === 0) return null;
                                return (
                                    <div key={section.key} className="rcr-report-section">
                                        <Divider orientation="left">
                                            <Tag color={section.color} style={{ fontSize: 14, padding: '4px 12px' }}>
                                                {section.title}
                                            </Tag>
                                        </Divider>
                                        <Table
                                            columns={buildMonthlyColumns(section.key)}
                                            dataSource={section.data}
                                            rowKey={(r, i) => `${section.key}_${i}`}
                                            pagination={false}
                                            size="small"
                                            bordered
                                            scroll={{ x: 'max-content' }}
                                            className="rcr-report-table"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card>
            )}
        </div>
    );
};

export default ReportGeneratorCR;